import React, { useState, useCallback } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { PostCard } from '../PostCard';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { Post, postService } from '../../services/postService';
import { PostSkeleton } from '../PostSkeleton';
import { EmptyState } from '../EmptyState';
import type { ReactionType } from '../ReactionPicker';
import { websocketService } from '../../services/websocketService';
import { debugLogger } from '../../utils/debugLogger';
import { authService as apiAuthService } from '../../services/api';
import { apiClient } from '../../services/apiClient';
import { useFeedWebSocket } from '../../hooks/usePostWebSocket';

interface UserFeedProps {
  className?: string;
  posts?: Post[]; // Allow external posts
  onDeletePost?: (postId: number) => void; // Callback for post deletion
  onTokenValidationFailed?: () => void; // Callback when token validation fails
  moodFilter?: import('../../services/postService').Mood | 'all'; // Filter posts by mood
  topicFilter?: import('../../services/postService').Topic | string | 'all'; // Filter posts by topic
  onNewPost?: (addNewPostCallback: (post: Post) => void) => void; // Pass addNewPost function to parent
}

export const UserFeed: React.FC<UserFeedProps> = ({
  className = '',
  posts: externalPosts,
  onDeletePost,
  onTokenValidationFailed,
  moodFilter = 'all',
  topicFilter = 'all',
  onNewPost
}) => {
  const { isAuthenticated, user } = useAuth();
  const [internalPosts, setInternalPosts] = useState<Post[]>([]);
  const allPosts = (externalPosts && Array.isArray(externalPosts)) ? externalPosts : internalPosts;
  
  // Apply mood and topic filters
  let filteredPosts = allPosts;
  
  if (moodFilter !== 'all') {
    filteredPosts = filteredPosts.filter(post => post.mood === moodFilter);
  }
  
  if (topicFilter !== 'all') {
    filteredPosts = filteredPosts.filter(post => post.topic === topicFilter);
  }
  
  const posts = filteredPosts;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [retryCount, setRetryCount] = useState(0);
  const [isRetrying, setIsRetrying] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [pendingPosts, setPendingPosts] = useState<Set<number>>(new Set());
  const [tokenValidated, setTokenValidated] = useState<boolean | null>(null);
  
  const isLoadingRef = React.useRef(false);
  const hasInitialLoadedRef = React.useRef(false);
  const tokenValidationRef = React.useRef(false);

  // Realtime feed updates
  const { newPostsAvailable, newPostsCount, latestPost, loadNewPosts } = useFeedWebSocket();

  // Handle new post from WebSocket
  React.useEffect(() => {
    if (latestPost && !externalPosts) {
      debugLogger.log('UserFeed', '✨ New post available from feed', latestPost);
    }
  }, [latestPost, externalPosts]);

  const loadPosts = useCallback(async (pageToLoad: number, isRetry = false) => {
    if (!isAuthenticated || isLoadingRef.current || externalPosts) {
      debugLogger.log('UserFeed', '⏭️ Skipping loadPosts', { 
        isAuthenticated, 
        isLoading: isLoadingRef.current, 
        hasExternalPosts: !!externalPosts
      });
      return;
    }

    isLoadingRef.current = true;
    setIsLoading(true);
    debugLogger.log('UserFeed', '📥 Loading personalized feed from /api/posts/feed/personalized', { page: pageToLoad });
    
    if (!isRetry) {
      setError(null);
    }

    try {
      // Use personalized feed endpoint for better content recommendations
      const response = await postService.getPersonalizedFeed(pageToLoad, 10);
      
      // Validate response structure
      if (!response || !response.content || !Array.isArray(response.content)) {
        console.error('Invalid response structure:', response);
        throw new Error('Invalid response from server');
      }
      
      setInternalPosts(prev => {
        const existingIds = new Set(prev.map(post => post.id));
        const newPosts = response.content.filter(post => !existingIds.has(post.id));
        
        if (newPosts.length !== response.content.length) {
          console.warn(`Filtered out ${response.content.length - newPosts.length} duplicate posts on page ${pageToLoad}`);
        }
        
        debugLogger.log('UserFeed', '📦 Posts updated', {
          prevCount: prev.length,
          newCount: newPosts.length,
          totalCount: prev.length + newPosts.length
        });
        
        return [...prev, ...newPosts];
      });
      
      const newHasMore = response.number < response.totalPages - 1;
      
      debugLogger.log('UserFeed', '📊 Pagination info', {
        currentPage: response.number,
        totalPages: response.totalPages,
        hasMore: newHasMore,
        calculation: `${response.number} < ${response.totalPages - 1}`
      });
      
      setPage(pageToLoad);
      setHasMore(newHasMore);
      setInitialLoadDone(true);
      hasInitialLoadedRef.current = true;
      
      setRetryCount(0);
      setIsRetrying(false);
    } catch (err) {
      console.error('Error loading posts:', err);
      setError('Không thể tải bài viết. Vui lòng thử lại sau.');
      setRetryCount(0);
      setInitialLoadDone(true);
      hasInitialLoadedRef.current = true;
    } finally {
      debugLogger.log('UserFeed', '✅ Load complete', { page: pageToLoad, postsCount: internalPosts.length });
      setIsLoading(false);
      isLoadingRef.current = false;
    }
  }, [isAuthenticated, externalPosts]);

  const loadMorePosts = useCallback(() => {
    console.log('📥 loadMorePosts called', {
      isLoadingRef: isLoadingRef.current,
      isLoading,
      hasMore,
      page,
      externalPosts: !!externalPosts
    });
    
    if (isLoadingRef.current) {
      console.log('⏭️ Skip loadMore - already loading (ref)');
      debugLogger.log('UserFeed', '⏭️ Skip loadMore - already loading');
      return;
    }
    
    if (!hasMore) {
      console.log('⏭️ Skip loadMore - no more posts');
      debugLogger.log('UserFeed', '⏭️ Skip loadMore - no more posts');
      return;
    }
    
    if (externalPosts) {
      console.log('⏭️ Skip loadMore - using external posts');
      debugLogger.log('UserFeed', '⏭️ Skip loadMore - using external posts');
      return;
    }
    
    console.log('🚀 Loading more posts', { currentPage: page, nextPage: page + 1 });
    debugLogger.log('UserFeed', '🚀 Loading more posts', { currentPage: page, nextPage: page + 1 });
    loadPosts(page + 1);
  }, [hasMore, page, externalPosts, loadPosts, isLoading]); 

  const addNewPost = useCallback((post: Post) => {
    debugLogger.log('UserFeed', '✨ Adding new post to feed', { postId: post.id });
    setInternalPosts(prev => {
      const exists = prev.some(p => p.id === post.id);
      if (exists) {
        debugLogger.log('UserFeed', 'Post already exists in feed, skipping', { postId: post.id });
        return prev;
      }
      return [post, ...prev];
    });
  }, []);

  React.useEffect(() => {
    if (onNewPost && !externalPosts) {
      debugLogger.log('UserFeed', '📤 Passing addNewPost callback to parent');
      onNewPost(addNewPost);
    }
  }, [onNewPost, addNewPost, externalPosts]);

  // Initialize personalized feed
  React.useEffect(() => {
    if (isAuthenticated && !externalPosts && tokenValidated) {
      debugLogger.log('UserFeed', '🔄 Personalized feed initialized, loading posts');
      setInternalPosts([]);
      setPage(0);
      setHasMore(true);
      hasInitialLoadedRef.current = false;
      loadPosts(0);
    }
  }, [isAuthenticated, externalPosts, tokenValidated, loadPosts]);

  // Validate token before loading posts
  React.useEffect(() => {
    const validateToken = async () => {
      if (tokenValidationRef.current || externalPosts || !isAuthenticated) {
        debugLogger.log('UserFeed', 'Skipping token validation', { 
          alreadyValidated: tokenValidationRef.current, 
          hasExternalPosts: !!externalPosts, 
          isAuthenticated 
        });
        return;
      }

      tokenValidationRef.current = true;
      debugLogger.log('UserFeed', '🔐 Starting token validation: POST /api/auth/validate');
      
      try {
        const isValid = await apiAuthService.validateToken();
        debugLogger.log('UserFeed', isValid ? '✅ Token validation SUCCESS' : '❌ Token validation FAILED', { isValid });
        setTokenValidated(isValid);
        
        if (!isValid) {
          debugLogger.log('UserFeed', '⚠️ Token invalid - clearing tokens and switching to public feed');
          // Clear invalid token from both memory and localStorage
          apiClient.clearTokens();
          
          // Notify parent component that token validation failed
          if (onTokenValidationFailed) {
            debugLogger.log('UserFeed', '📢 Notifying parent: token validation failed');
            onTokenValidationFailed();
          }
        } else {
          debugLogger.log('UserFeed', '🎉 Token validated successfully - triggering initial load immediately');
          // Call loadPosts immediately after validation to avoid race condition
          if (!hasInitialLoadedRef.current) {
            loadPosts(0);
          }
        }
      } catch (error) {
        debugLogger.log('UserFeed', '💥 Token validation error (network/API issue):', error);
        setTokenValidated(false);
        
        // Clear tokens on validation error from both memory and localStorage
        apiClient.clearTokens();
        
        // Notify parent component that token validation failed
        if (onTokenValidationFailed) {
          debugLogger.log('UserFeed', '📢 Notifying parent: token validation error');
          onTokenValidationFailed();
        }
      }
    };

    validateToken();
  }, [isAuthenticated, externalPosts, onTokenValidationFailed, loadPosts]);

  React.useEffect(() => {
    if (!user || !isAuthenticated) {
      return;
    }

    const handlePostUpdate = (message: any) => {
      debugLogger.log('UserFeed', 'Received post update via WebSocket:', message);

      if (message.type === 'POST_UPDATED') {
        const updatedPost = message.data;
        debugLogger.log('UserFeed', 'Post updated successfully:', updatedPost);

        setInternalPosts(prev => {
          // Check if post already exists
          const existingIndex = prev.findIndex(p => p.id === updatedPost.id);
          
          if (existingIndex >= 0) {
            // Update existing post
            const newPosts = [...prev];
            newPosts[existingIndex] = updatedPost;
            return newPosts;
          } else {
            // Add new post to the top
            return [updatedPost, ...prev];
          }
        });

        // Remove from pending
        setPendingPosts(prev => {
          const newSet = new Set(prev);
          newSet.delete(message.postId);
          return newSet;
        });
      } else if (message.type === 'POST_UPDATE_FAILED') {
        debugLogger.log('UserFeed', 'Post update failed:', message);
        
        // Mark post as failed
        setInternalPosts(prev => prev.map(p => 
          p.id === message.postId ? { ...p, createFailed: true, isPending: false } : p
        ));

        // Remove from pending
        setPendingPosts(prev => {
          const newSet = new Set(prev);
          newSet.delete(message.postId);
          return newSet;
        });
      }
    };

    // Subscribe to user's post updates
    websocketService.subscribeToUserPosts(user.id, handlePostUpdate);

    return () => {
      websocketService.unsubscribeFromUserPosts(user.id);
    };
  }, [user, isAuthenticated]);

  // Mark as loaded if using external posts
  React.useEffect(() => {
    if (externalPosts) {
      setInitialLoadDone(true);
    }
  }, [externalPosts]);



  // Debug: Log state changes
  React.useEffect(() => {
    console.log('📊 UserFeed infinite scroll state:', {
      hasMore,
      isLoading,
      isLoadingRef: isLoadingRef.current,
      page,
      postsCount: posts.length
    });
  }, [hasMore, isLoading, page, posts.length]);

  // Infinite scroll with larger threshold for better UX
  useInfiniteScroll({
    hasMore,
    isLoading,
    onLoadMore: loadMorePosts,
    threshold: 800 // Trigger earlier (800px before bottom)
  });

  const handleReaction = async (postId: number, reaction: ReactionType) => {
    try {
      await postService.reactToPost(postId, { type: reaction });
      setInternalPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const updatedPost = { ...post };
          if (!updatedPost.reactions) {
            updatedPost.reactions = {
              totalCount: 0,
              reactionCounts: [],
              recentReactions: [],
              currentUserReacted: false,
              currentUserReactionType: null
            };
          }
          updatedPost.reactions.totalCount = (updatedPost.reactions.totalCount || 0) + 1;
          updatedPost.reactions.currentUserReacted = true;
          updatedPost.reactions.currentUserReactionType = reaction;
          return updatedPost;
        }
        return post;
      }));
    } catch (err) {
      console.error('Error reacting to post:', err);
    }
  };

  const handleUnreact = async (postId: number) => {
    try {
      await postService.unreactPost(postId);
      setInternalPosts(prev => prev.map(post => {
        if (post.id === postId) {
          const updatedPost = { ...post };
          if (!updatedPost.reactions) {
            updatedPost.reactions = {
              totalCount: 0,
              reactionCounts: [],
              recentReactions: [],
              currentUserReacted: false,
              currentUserReactionType: null
            };
          }
          updatedPost.reactions.totalCount = Math.max((updatedPost.reactions.totalCount || 0) - 1, 0);
          updatedPost.reactions.currentUserReacted = false;
          updatedPost.reactions.currentUserReactionType = null;
          return updatedPost;
        }
        return post;
      }));
    } catch (err) {
      console.error('Error unreacting to post:', err);
    }
  };

  const handleComment = async (postId: number, content: string) => {
    try {
      setInternalPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            commentCount: post.commentCount + 1
          };
        }
        return post;
      }));
    } catch (err) {
      console.error('Error commenting on post:', err);
    }
  };

  const handleShare = async (postId: number) => {
    try {
      await postService.sharePost(postId, {});
      setInternalPosts(prev => prev.map(post => {
        if (post.id === postId) {
          return {
            ...post,
            shareCount: post.shareCount + 1
          };
        }
        return post;
      }));
    } catch (err) {
      console.error('Error sharing post:', err);
    }
  };

  const handleRetry = () => {
    setError(null);
    setRetryCount(0);
    setPage(0);
    setInternalPosts([]);
    setHasMore(true);
    setInitialLoadDone(false);
    hasInitialLoadedRef.current = false;
    isLoadingRef.current = false;
    loadPosts(0);
  };

  const handleDelete = (postId: number) => {
    if (onDeletePost) {
      onDeletePost(postId);
    } else {
      setInternalPosts((prev) => prev.filter((post) => post.id !== postId));
    }
  };

  const handlePostUpdated = (updatedPost: Post) => {
    if (externalPosts) {
      return;
    }
    setInternalPosts((prev) =>
      prev.map((post) => (post.id === updatedPost.id ? { ...post, ...updatedPost } : post))
    );
  };

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center p-6 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h3 className="text-lg font-medium text-gray-900 mb-2">Lỗi tải bài viết</h3>
        <p className="text-gray-500 mb-4">{error}</p>
        <button
          onClick={handleRetry}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
        >
          Thử lại
        </button>
      </div>
    );
  }

  // If token validation failed, return null to let parent component handle (show public posts)
  if (tokenValidated === false) {
    return null;
  }

  // Show skeleton while waiting for token validation or initial load
  if ((isLoading || !initialLoadDone) && (!posts || posts.length === 0)) {
    return (
      <div className={className}>
        <PostSkeleton />
        <PostSkeleton />
        <PostSkeleton />
      </div>
    );
  }

  if (!isLoading && (!posts || posts.length === 0) && initialLoadDone && tokenValidated === true) {
    return (
      <EmptyState
        type="empty"
        title="Chưa có bài viết nào"
        description="Hãy theo dõi người khác hoặc tham gia nhóm để xem bài viết trong feed của bạn"
      />
    );
  }

  return (
    <div className={className}>
      {/* New Posts Banner */}
      {newPostsAvailable && !externalPosts && (
        <button
          onClick={() => {
            loadNewPosts();
            // Reload feed from top
            setInternalPosts([]);
            setPage(0);
            setHasMore(true);
            hasInitialLoadedRef.current = false;
            loadPosts(0);
          }}
          className="sticky top-20 z-10 w-full mb-4 bg-gradient-to-r from-blue-500 to-indigo-600 hover:from-blue-600 hover:to-indigo-700 text-white px-6 py-3 rounded-xl shadow-lg transition-all transform hover:scale-[1.02] flex items-center justify-center space-x-2 animate-pulse"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11l5-5m0 0l5 5m-5-5v12" />
          </svg>
          <span className="font-semibold">
            {newPostsCount} bài viết mới • Nhấn để tải
          </span>
        </button>
      )}

      {posts && Array.isArray(posts) && posts.map((post, index) => (
        <div 
          key={`feed-post-${post.id}-${index}`}
          className="animate-fade-in-scale"
          style={{ animationDelay: `${Math.min(index * 50, 300)}ms` }}
        >
          <PostCard
            post={post}
            onReaction={handleReaction}
            onUnreact={handleUnreact}
            onComment={handleComment}
            onShare={handleShare}
            onDelete={() => handleDelete(post.id)}
            currentUser={user || undefined}
            currentUserId={user?.id}
            enableEditing
            onPostUpdated={handlePostUpdated}
          />
        </div>
      ))}
      
      {/* Loading more posts - Always show when loading */}
      {isLoading && posts && posts.length > 0 && (
        <div className="flex justify-center py-8">
          <div className="flex flex-col items-center space-y-3">
            <div className="relative">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-blue-200"></div>
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-transparent border-t-blue-600 absolute top-0 left-0"></div>
            </div>
            <p className="text-sm text-gray-500 font-medium">Đang tải thêm bài viết...</p>
          </div>
        </div>
      )}

      {/* No more posts indicator */}
      {!hasMore && posts && posts.length > 0 && !isLoading && (
        <div className="text-center py-8">
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 max-w-md mx-auto border border-blue-100">
            <div className="flex flex-col items-center space-y-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-800 mb-1">Bạn đã xem hết tất cả bài viết</p>
                <p className="text-xs text-gray-500">Hãy theo dõi thêm bạn bè hoặc tham gia nhóm để xem nhiều nội dung hơn!</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Infinite scroll load trigger zone */}
      {hasMore && !isLoading && posts && posts.length > 0 && (
        <div 
          className="h-96 flex items-center justify-center"
          style={{ minHeight: '400px' }}
        >
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 text-gray-400 mb-4">
              <div className="animate-pulse w-2 h-2 bg-blue-500 rounded-full"></div>
              <span className="text-sm">Đang tải thêm bài viết...</span>
            </div>
            <button
              onClick={() => {
                console.log('👆 Manual load button clicked');
                loadMorePosts();
              }}
              className="bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white px-6 py-2 rounded-lg transition-all transform hover:scale-105 shadow-md hover:shadow-lg text-sm font-medium"
            >
              <span className="flex items-center space-x-2">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                <span>Tải ngay</span>
              </span>
            </button>
          </div>
        </div>
      )}
      
      {/* Retry indicator - removed duplicate logic, handled in loading section above */}
    </div>
  );
};
